import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, ShoppingBag, MapPin, Clock, AlertCircle } from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useAuth } from '../contexts/AuthContext'
import { getEvent, supabase } from '../lib/supabase'
import { formatPrice } from '../lib/square'
import PaymentForm from '../components/payment/PaymentForm'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ReservationTimer from '../components/checkout/ReservationTimer'
import BackgroundSlideshow from '../components/BackgroundSlideshow'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'
import { getAssetUrl } from '../../hooks/useAsset'

interface Reservation {
  reservation_id: string
  expires_at: string
  remaining_capacity: number
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { items, subtotal, taxRate, taxAmount, tipAmount, totalAmount, totalLobsterTails, eventId, clearCart, setTaxRate, setTipAmount } = useCart()
  const [event, setEvent] = useState<any>(null)
  const [capacity, setCapacity] = useState<any>(null)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Background slideshow images
  const slideshowImages = [
    getAssetUrl('/images/TT-IMG1.jpg'),
    getAssetUrl('/images/TT-IMG2.jpg'),
    getAssetUrl('/images/TT-IMG3.jpg'),
    getAssetUrl('/images/TT-IMG4.jpg'),
    getAssetUrl('/images/TT-IMG5.jpg')
  ]

  // Reservation state
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [reservationLoading, setReservationLoading] = useState(true)
  const [reservationError, setReservationError] = useState<string | null>(null)
  
  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0) {
      navigate('/events')
    }
  }, [items.length, navigate])

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login')
    }
  }, [user, navigate])

  // CREATE RESERVATION ON CHECKOUT LOAD
  useEffect(() => {
    const abortController = new AbortController()

    const createReservation = async () => {
      if (!user || !eventId || items.length === 0) {
        if (!abortController.signal.aborted) {
          setReservationLoading(false)
        }
        return
      }

      try {
        // Get current session for auth token
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) {
          throw new Error('No active session')
        }

        // Format cart items for reservation API
        const cartItems = items.map(item => ({
          menuItemId: item.menuItem.id,
          quantity: item.quantity
        }))

        // Call create-reservation Netlify function
        const response = await fetch('/.netlify/functions/create-reservation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            eventId,
            cartItems
          }),
          signal: abortController.signal
        })

        const data = await response.json()

        if (abortController.signal.aborted) return

        if (!response.ok) {
          // Handle specific error cases
          if (data.code === 'CAPACITY_EXCEEDED') {
            setReservationError('This event is sold out or does not have enough capacity for your order.')
          } else if (data.code === 'PER_ORDER_LIMIT_EXCEEDED') {
            setReservationError(data.error || 'Your order exceeds the per-order limit.')
          } else if (data.code === 'EVENT_NOT_ACTIVE') {
            setReservationError(data.error || 'This event is not currently accepting orders.')
          } else if (data.error?.includes('lobster_tail_qty')) {
            setReservationError('Some items in your cart have invalid configuration. Please refresh and try again.')
          } else if (data.error?.includes('reserved_tail_count')) {
            setReservationError('Unable to calculate order capacity. Please refresh your cart and try again.')
          } else {
            setReservationError(data.error || 'Unable to reserve your items. Please try again.')
          }
          return
        }

        // Reservation created successfully
        setReservation(data)
        setReservationError(null)

      } catch (error: any) {
        if (abortController.signal.aborted) return
        console.error('Failed to create reservation:', error)
        setReservationError(error.message || 'Unable to reserve your items. Please try again.')
      } finally {
        if (!abortController.signal.aborted) {
          setReservationLoading(false)
        }
      }
    }

    createReservation()

    return () => {
      abortController.abort()
    }
  }, [user, eventId, items])

  // Phase 4: Load event data and capacity for validation
  useEffect(() => {
    const abortController = new AbortController()

    const loadEventAndCapacity = async () => {
      if (!eventId) return

      try {
        // Load event
        const eventData = await getEvent(eventId)

        if (abortController.signal.aborted) return

        setEvent(eventData)

        // Tax & Tip: Set tax rate from event
        if (eventData?.tax_rate !== undefined) {
          setTaxRate(eventData.tax_rate)
        }

        // Load capacity if event has lobster tail limits
        if (eventData?.max_lobster_tails) {
          const { data: { session: capSession } } = await supabase.auth.getSession()
          const response = await fetch(`/.netlify/functions/get-event-capacity?event_id=${eventId}`, {
            signal: abortController.signal,
            headers: capSession?.access_token
              ? { 'Authorization': `Bearer ${capSession.access_token}` }
              : {}
          })

          if (abortController.signal.aborted) return

          if (response.ok) {
            const capacityData = await response.json()
            setCapacity(capacityData)
          }
        }
      } catch (error: any) {
        if (abortController.signal.aborted) return
        console.error('Failed to load event/capacity:', error)
      }
    }

    loadEventAndCapacity()

    return () => {
      abortController.abort()
    }
  }, [eventId, setTaxRate])

  // Phase 4: Validate cart against capacity limits
  useEffect(() => {
    if (!event) return

    setValidationError(null)

    // Check per-order tail limit
    if (event.max_tails_per_order && totalLobsterTails > event.max_tails_per_order) {
      setValidationError(
        `Your cart contains ${totalLobsterTails} lobster tails, but the maximum per order is ${event.max_tails_per_order}. Please reduce your order.`
      )
      return
    }

    // Check remaining capacity
    if (capacity && capacity.remaining_capacity !== null && totalLobsterTails > capacity.remaining_capacity) {
      setValidationError(
        `Only ${capacity.remaining_capacity} lobster tails remain, but your cart has ${totalLobsterTails}. Please reduce your order.`
      )
      return
    }

    // Check if sold out
    if (capacity?.is_sold_out) {
      setValidationError('This event is sold out. No more orders can be placed.')
      return
    }
  }, [event, capacity, totalLobsterTails])

  /**
   * Enterprise-grade order placement with capacity check
   * Uses RPC function with row-level locking to prevent race conditions
   */
  const placeOrderWithCapacityCheck = useCallback(async () => {
    if (!user || !eventId) {
      throw new Error('User or event not found')
    }

    // Prepare order items in RPC format
    const orderItems = items.map(item => ({
      menu_item_id: item.menuItem.id,
      quantity: item.quantity
    }))

    // Get current session for auth token
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      throw new Error('No active session')
    }

    // Call secure Netlify function that uses place_order_with_capacity_check RPC
    const response = await fetch('/.netlify/functions/place-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        eventId,
        orderItems
      })
    })

    const result = await response.json()

    if (!response.ok) {
      // Handle specific error cases from the RPC
      if (result.code === 'CAPACITY_EXCEEDED') {
        throw new Error('This event is now sold out. Another customer just reserved the last spots.')
      }
      if (result.code === 'PER_ORDER_LIMIT_EXCEEDED') {
        throw new Error(result.message || 'Your order exceeds the per-order limit.')
      }
      throw new Error(result.message || result.error || 'Failed to create order')
    }

    return result.orderId
  }, [user, eventId, items, totalAmount])

  const handlePaymentSuccess = useCallback(async (orderId: string, paymentResult: any) => {
    // Clear cart and redirect to success
    clearCart()
    toast.custom((t) => (
      <EnterpriseToast t={t} type="success" title="Order Placed" message="Your order has been confirmed!" />
    ))
    navigate(`/orders/${orderId}`)
  }, [clearCart, navigate])

  const handlePaymentError = useCallback((error: string) => {
    toast.custom((t) => (
      <EnterpriseToast t={t} type="error" title="Payment Failed" message={error} />
    ))
  }, [])

  const handleReservationExpire = useCallback(() => {
    setReservationError('Your reservation has expired. Please return to the event to place a new order.')
    setReservation(null)
    toast.custom((t) => (
      <EnterpriseToast
        t={t}
        type="error"
        title="Reservation Expired"
        message="Your items are no longer reserved. Please try again."
      />
    ))
  }, [])

  // Show loading while checking auth or creating reservation
  if (!user || items.length === 0 || reservationLoading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          {reservationLoading && (
            <p className="mt-4 text-muted-foreground">Reserving your items...</p>
          )}
        </div>
      </div>
    )
  }

  // Show error if reservation failed
  if (reservationError && !reservation) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background">
        <div className="max-w-md mx-auto p-6">
          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-bold text-foreground mb-2">Unable to Reserve Items</h2>
            <p className="text-muted-foreground mb-6">{reservationError}</p>
            <button
              onClick={() => navigate(-1)}
              className="secret-btn-primary"
            >
              Back to Event
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe relative">
      {/* Background Slideshow */}
      <BackgroundSlideshow
        images={slideshowImages}
        interval={6000}
      />

      {/* Header */}
      <header className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>

            <h1 className="text-xl font-bold text-foreground border-b-4 border-maroon-800 pb-2">Checkout</h1>

            <div className="w-16" /> {/* Spacer for center alignment */}
          </div>
        </div>
      </header>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        {/* Logo */}
        <div className="flex justify-center my-8">
          <img
            src={getAssetUrl('/tailtations.svg')}
            alt="Tailtations Logo"
            className="w-[300px]"
          />
        </div>

        {/* Reservation Timer */}
        {reservation && (
          <ReservationTimer
            expiresAt={reservation.expires_at}
            onExpire={handleReservationExpire}
          />
        )}

        {/* Order Summary */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Order Summary</h3>

          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.menuItem.id} className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{item.menuItem.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Quantity: {item.quantity} × {formatPrice(item.menuItem.price * 100)}
                  </p>
                </div>
                <div className="text-gray-900 dark:text-gray-100 font-semibold">
                  {formatPrice(item.menuItem.price * item.quantity * 100)}
                </div>
              </div>
            ))}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 space-y-2">
              <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                <span>Subtotal</span>
                <span className="font-semibold">{formatPrice(subtotal * 100)}</span>
              </div>
              <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                <span>Tax ({(taxRate * 100).toFixed(1)}%)</span>
                <span className="font-semibold">{formatPrice(taxAmount * 100)}</span>
              </div>
              {tipAmount > 0 && (
                <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                  <span>Tip</span>
                  <span className="font-semibold">{formatPrice(tipAmount * 100)}</span>
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border-2 border-maroon-800 mt-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center">TOTAL AMOUNT</p>
                <p className="text-4xl font-extrabold text-gray-900 dark:text-white text-center mt-1">
                  {formatPrice(totalAmount * 100)}
                </p>
              </div>

              {/* Phase 4: Display total lobster tails */}
              {event?.max_lobster_tails && totalLobsterTails > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4 flex justify-between items-center">
                  <span className="text-md font-medium text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                    <span>🦞</span>
                    <span>Total Lobster Tails</span>
                  </span>
                  <span className="text-2xl font-bold text-maroon-800 dark:text-maroon-400">{totalLobsterTails}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Phase 4: Validation Error */}
        {validationError && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-8">
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="text-sm font-semibold text-red-500 mb-1">Cannot Complete Order</h4>
                <p className="text-xs text-red-400">{validationError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Event Information */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="text-maroon-800" size={20} />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Pickup Information</h3>
          </div>

          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-2">
              <Clock size={16} />
              <span>You'll receive pickup instructions via notification</span>
            </div>
            <p>Present your QR code at pickup to collect your order.</p>
          </div>
        </div>

        {/* Payment Section */}
        {validationError ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Payment</h3>
            <div className="text-center py-8">
              <AlertCircle className="text-red-500 mx-auto mb-3" size={48} />
              <p className="text-muted-foreground">
                Please resolve the issues above before proceeding to payment.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 px-6 py-2 bg-secret-primary text-white rounded-lg hover:bg-secret-primary-dark transition-colors"
              >
                Back to Event
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">Payment</h3>
            <PaymentForm
              amount={totalAmount * 100} // Convert to cents
              reservationId={reservation?.reservation_id || null}
              placeOrder={placeOrderWithCapacityCheck}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              subtotal={subtotal}
              taxRate={taxRate}
              taxAmount={taxAmount}
              tipAmount={tipAmount}
              onTipChange={setTipAmount}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-4 bg-white dark:bg-black text-gray-900 dark:text-white border-t border-gray-200 dark:border-white/20">
        <div className="container mx-auto max-w-2xl">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-700 dark:text-white/90">
              &copy; 2025 Tailtations LLC. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link
                to="/privacy-policy"
                className="text-gray-700 dark:text-white/90 hover:text-gray-900 dark:hover:text-white/80 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-use"
                className="text-gray-700 dark:text-white/90 hover:text-gray-900 dark:hover:text-white/80 transition-colors"
              >
                Terms of Use
              </Link>
              <Link
                to="/terms-of-sale"
                className="text-gray-700 dark:text-white/90 hover:text-gray-900 dark:hover:text-white/80 transition-colors"
              >
                Terms of Sale
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default CheckoutPage